// constants
import {
    CHANGE_PAYMENT_CTA_ARIA_LABEL,
    INSTANT_PAYMENT_FOP_LIST,
    INSTANT_PAYMENT_SUBTEXT_MAX_IMPRESSION_COUNT,
    INSTANT_PAYMENT_SUBTEXT_SHOWN,
    SLIDE_TO_PAY_AMOUNT,
} from "@payment-ui/constants/TurboPayment";
import { PLACE_ORDER_EXTRA_PARAMS, TEXTS } from "@payment-ui/constants";
// interfaces
import {
    CardPaymentMethodData,
    ITurboPaymentMethodProps,
    TurboCodPaymentMethodMeta,
    WalletPaymentGroupActions,
} from "@payment-ui/interfaces";
// styled-components
import {
    Container,
    Flex,
    KfsDisclaimerContainer,
    KfsLink,
    PaymentMethodContainer,
    PaymentMethodPayButtonContainer,
    StyledText,
    PaymentIconV2,
} from "./styles";
// internal-deps
import { LocalStorage, format, useEffectOnce } from "@swiggy-private/core-ui";
import { isEmpty } from "@swiggy-private/js-utils";
// deps
import React, { memo, useCallback, useContext, useMemo, useState } from "react";
import { CappingModes } from "@swiggy-private/native-bridge";

// helpers
import { updateFrequencyCount } from "@swiggy-private/checkout-core";
import { getCardPaymentMethodExtraParams, isCardPaymentMethod, isFoodCard } from "@payment-ui/helpers/Card";
import {
    getInAppPaymentMethodExtraParams,
    isInAppUpiLitePaymentMethod,
    isInAppUpiPaymentMethod,
} from "@payment-ui/helpers/InAppUpi";
import { getWalletPaymentMethodExtraParams, isWalletPaymentMethod } from "@payment-ui/helpers/Wallet";
import { getCvvRequired } from "@payment-ui/helpers/Cvv";
import { getTurboPaymentPresentationalInfo } from "@payment-ui/helpers/TurboPayment";
import { isCODPaymentMethod } from "@payment-ui/helpers/Cod";
import { isAmazonPayLater } from "@payment-ui/helpers/PayLater";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
// test id
import { TURBO_PAYMENT_METHOD_WIDGET_DATA_TEST_IDS } from "@swiggy-private/payment-test-ids";

// widgets/components
import CvvBottomSheet from "@payment-ui/components/cvv-bottom-sheet/CVVBottomSheet";
import { DEFAULT_CARD_DATA_STATE } from "@payment-ui/components/payment-method/card/constants";
import PodConsentHalfCard from "@payment-ui/components/turbo-payment-widget/components/pod-half-card";
import { TurboPaymentContext } from "@payment-ui/components/turbo-payment-widget/TurboPaymentContext";
import TurboPaymentDisplayName from "../../turbo-payment-display-name/v2";
import TurboPaymentMethodMetaWidget from "../../turbo-payment-method-meta-widget/v2";
import { TurboV2PaymentButton } from "../../turbo-v2-pay-button";
import { TurboStrip } from "@swiggy-private/core-ui";
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { default as chevronUpFilled } from "@swiggy-private/dls-web-icons/dist/web/generic/chevronUpFilled/12";

//TODO: break this component into smaller components while cleaning up
function TurboPaymentMethodWidget(props: ITurboPaymentMethodProps) {
    const {
        methodData,
        turboPaymentCtaMeta,
        paymentActions,
        onClickChangePaymentCta,
        balanceValue,
        showDiscountedAmount,
        turboPodHalfCardEvents,
        isPopCart,
        onClickTurboStrip,
        onImpressionTurboStrip,
        turboStrip = {},
        isTurboV2,
        isBillToCompanyExist,
        onCvvPopupOpen,
        keyFactStatementUrl,
        onKeyFactStatementClick,
    } = props;

    const {
        iconUrl,
        methodDisplayName,
        promoMsg,
        discount,
        balanceMeta,
        showInlineBalance,
        cardSubName,
        showNewLineBalance,
        payLabel,
    } = getTurboPaymentPresentationalInfo(methodData, balanceValue);
    const { onSlideButtonSuccess } = useContext(TurboPaymentContext);

    const toShowInstantPaymentSubtext = useMemo(() => {
        /* Instant Payment Subtext is to be shown beneath the Slider Text only for the cases
         * when the payment method is quick-payment and also the user is not seeing it more then 3 times */

        const groupName = methodData?.groupName?.toUpperCase();
        const isInstantPaymentFop =
            INSTANT_PAYMENT_FOP_LIST.includes(groupName) || isInAppUpiLitePaymentMethod(methodData); // Added separate check for UPI Lite since InAppUpi groupname is common for normal accounts and upi lite accounts
        const instantPaymentSubtextImpressionCount = LocalStorage.getItem(INSTANT_PAYMENT_SUBTEXT_SHOWN, 0) || 0;
        const toShowInstantPaymentSubtext =
            isInstantPaymentFop && instantPaymentSubtextImpressionCount < INSTANT_PAYMENT_SUBTEXT_MAX_IMPRESSION_COUNT;

        if (toShowInstantPaymentSubtext)
            LocalStorage.setItem(INSTANT_PAYMENT_SUBTEXT_SHOWN, instantPaymentSubtextImpressionCount + 1);

        return toShowInstantPaymentSubtext;
    }, [methodData]);

    const [showCvvPopup, setShowCvvPopup] = useState(false);
    const [showPodHalfCard, setPodHalfCard] = useState(false);

    // We wont be showing CRED related offer on turbo widget for initial release and will be taken up in future
    // const dynamicPromoMsg = useMemo(() => {
    //     if (!isCredPaymentMethod(methodData)) {
    //         return;
    //     }

    //     const meta = methodData.meta as CredPaymentMethodMeta;
    //     const message = credEligibleData?.isOfferLoading ? (
    //         <DynamicPromoSubText>
    //             {meta.loadingOfferMessage ?? LOADING_OFFER_CTA} <ThreeDotAnimation />
    //         </DynamicPromoSubText>
    //     ) : (
    //         <DynamicPromoSubText>{credEligibleData?.bannerText}</DynamicPromoSubText>
    //     );
    //     return message;
    // }, [methodData, credEligibleData]);

    const onPay = useCallback(
        (extraData = {}, cvv?: string): void => {
            //PLACE_ORDER_EXTRA_PARAMS.SWIGGY_PAY_AMOUNT is made undefined as swiggy money is not there in
            // for turbopayment currently
            const generalExtraParams = {
                [PLACE_ORDER_EXTRA_PARAMS.SWIGGY_PAY_AMOUNT]: undefined,
            };

            let paymentMethodExtraParams;

            if (isWalletPaymentMethod(methodData)) {
                paymentMethodExtraParams = getWalletPaymentMethodExtraParams(
                    methodData,
                    paymentActions as WalletPaymentGroupActions,
                );
            }

            if (isCardPaymentMethod(methodData)) {
                paymentMethodExtraParams = getCardPaymentMethodExtraParams(methodData, cvv);
            }

            if (isInAppUpiPaymentMethod(methodData)) {
                paymentMethodExtraParams = getInAppPaymentMethodExtraParams(methodData);
            }

            paymentActions.onPay(
                methodData,
                { ...generalExtraParams, ...paymentMethodExtraParams },
                {
                    isTurboPaymentMethod: true,
                    isTurboV2: true,
                    ...extraData,
                },
            );
        },
        [methodData, paymentActions],
    );

    const onPayButtonClick = useCallback(() => {
        onSlideButtonSuccess?.();

        if (isCardPaymentMethod(methodData) && !isFoodCard(methodData)) {
            // CVV not required for sodexo cards, one click Cards
            // Passing cardData  as DEFAULT_CARD_DATA since food card is excluded
            const isCvvRequired = getCvvRequired(methodData, DEFAULT_CARD_DATA_STATE);
            if (isCvvRequired) {
                onCvvPopupOpen?.();
                setShowCvvPopup(true);
                return;
            }
        }

        if (isCODPaymentMethod(methodData)) {
            const metadata = methodData.meta as TurboCodPaymentMethodMeta;

            if (!isEmpty(metadata.halfCard) && !isEmpty(metadata.halfCardInfo)) {
                setPodHalfCard(true);
                return;
            }
        }
        onPay();
    }, [methodData, onPay, onSlideButtonSuccess, onCvvPopupOpen]);

    const onCloseCvvBs = useCallback(() => {
        /* on dismissing CVV popup we need to close the popup and reset the slider */
        setShowCvvPopup(false);
    }, []);

    const onCardPayAction = useCallback(
        (cvv: string) => {
            setShowCvvPopup(false);
            // since this CVV popup will only be shown at end of slide payment, slide percent will be 100
            onPay({}, cvv);
        },
        [onPay],
    );

    const onTurboPodHalfCardPay = useCallback(() => {
        setPodHalfCard(false);
        onPay();
    }, [onPay]);

    const hideTurboPodHalfCard = useCallback(() => {
        setPodHalfCard(false);
    }, []);

    const showTurboStrip = !isEmpty(turboStrip) && !isBillToCompanyExist;

    const showKeyFactsDisclaimer = isAmazonPayLater(methodData.paymentCode) && !isEmpty(keyFactStatementUrl);

    const handleKeyFactsClick = useCallback(() => {
        if (keyFactStatementUrl && onKeyFactStatementClick) {
            onKeyFactStatementClick(keyFactStatementUrl);
        }
    }, [keyFactStatementUrl, onKeyFactStatementClick]);

    useEffectOnce(() => {
        if (showTurboStrip) {
            updateFrequencyCount(CappingModes.VIEW, turboStrip.bannerId || "");
        }
    });

    return (
        <>
            {showTurboStrip && (
                <TurboStrip
                    onClickTurboStrip={onClickTurboStrip}
                    onImpressionTurboStrip={onImpressionTurboStrip}
                    data={turboStrip}
                    isTurboV2={isTurboV2}
                />
            )}
            <Container>
                <PaymentMethodContainer>
                    <div
                        onClick={onClickChangePaymentCta}
                        data-testid={TURBO_PAYMENT_METHOD_WIDGET_DATA_TEST_IDS.CHANGE_CTA}
                        aria-label={CHANGE_PAYMENT_CTA_ARIA_LABEL}
                    >
                        <Flex>
                            <PaymentIconV2
                                aria-hidden="true"
                                data-testid={TURBO_PAYMENT_METHOD_WIDGET_DATA_TEST_IDS.PAYMENT_ICON}
                            >
                                <img alt="" crossOrigin="anonymous" src={iconUrl} height={12} />
                            </PaymentIconV2>
                            <StyledText variant="V2.overline.neutral.medium" color="text_Low_Emphasis">
                                {payLabel}
                            </StyledText>
                            <GenericIcon fillColor="text_Low_Emphasis" Icon={chevronUpFilled} />
                        </Flex>

                        <TurboPaymentDisplayName
                            displayName={methodDisplayName}
                            inlineBalanceMeta={showInlineBalance ? balanceMeta : undefined}
                            cardSubName={cardSubName}
                        />
                    </div>

                    <TurboPaymentMethodMetaWidget
                        promoMsg={promoMsg}
                        discount={discount}
                        newLineBalanceMeta={showNewLineBalance ? balanceMeta : undefined}
                    />
                </PaymentMethodContainer>
                <PaymentMethodPayButtonContainer>
                    <TurboV2PaymentButton
                        finalAmount={turboPaymentCtaMeta?.amountWithDiscount || ""}
                        onPayClick={onPayButtonClick}
                        discountAmount={
                            showDiscountedAmount && turboPaymentCtaMeta?.amountWithoutDiscount
                                ? format(SLIDE_TO_PAY_AMOUNT, turboPaymentCtaMeta.amountWithoutDiscount)
                                : undefined
                        }
                        showBolt={toShowInstantPaymentSubtext}
                        dataTestId={TURBO_PAYMENT_METHOD_WIDGET_DATA_TEST_IDS.PAY_BUTTON}
                        isPop={isPopCart}
                    />
                </PaymentMethodPayButtonContainer>
                {showCvvPopup && (
                    <CvvBottomSheet
                        cardSubName={cardSubName}
                        methodData={methodData as CardPaymentMethodData}
                        onCloseCvvBs={onCloseCvvBs}
                        onCardPayAction={onCardPayAction}
                    />
                )}
                {showPodHalfCard && (
                    <PodConsentHalfCard
                        isActive={showPodHalfCard}
                        data={methodData.meta as TurboCodPaymentMethodMeta}
                        onPlaceOrder={onTurboPodHalfCardPay}
                        onCancel={hideTurboPodHalfCard}
                        turboPodHalfCardEvents={turboPodHalfCardEvents}
                    />
                )}
            </Container>
            {showKeyFactsDisclaimer && (
                <KfsDisclaimerContainer>
                    <Text variant="V2.overline.neutral.regular" color="text_Med_Emphasis">
                        {TEXTS.KEY_FACTS_DISCLAIMER_TURBO}
                        <KfsLink role="link" tabIndex={0} onClick={handleKeyFactsClick}>
                            {TEXTS.KEY_FACTS_STATEMENT}
                        </KfsLink>
                        .
                    </Text>
                </KfsDisclaimerContainer>
            )}
        </>
    );
}
export default memo(TurboPaymentMethodWidget);
